import {
  CalculationMethodEnum,
  IncentiveStructureEnum,
  ProgramStatusEnum,
  ValidationRateEnum,
  YesNoEnum,
  AssignCMCoordinatorEnum,
  FieldVisibilityEnum,
} from "@/domain/programs.enum";
import { TFunction } from "i18next";
import * as yup from "yup";
import { InferType } from "yup";

const createProgramSchema = (t: TFunction) => {
  return yup.lazy(() => {
    const schemaFields: yup.ObjectShape = {
      // general information
      name: yup
        .string()
        .required(t("validations.required"))
        .min(2, t("validations.min_length", { count: 2 }))
        .max(50, t("validations.max_length", { count: 50 })),
      status: yup
        .string()
        .oneOf(
          [ProgramStatusEnum.ACTIVE, ProgramStatusEnum.CANCELLED],
          t("validations.invalid_format"),
        )
        .required(t("validations.required")),
      cmRoles: yup
        .array()
        .of(yup.string())
        .transform((value) =>
          typeof value === "string" && value === "" ? [] : value,
        )
        .test(
          "not-empty",
          "This field is required",
          (value) => Array.isArray(value) && value.length > 0,
        )
        .required(t("validations.required")),
      additionalStaffRoles: yup
        .array()
        .of(yup.string())
        .transform((value) =>
          typeof value === "string" && value === "" ? [] : value,
        ),

      // program numbers
      maxParticipants: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      programDuration: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      programBudgetLimit: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      // program ous
      orgUnitIds: yup
        .array()
        .of(yup.string())
        .transform((value) =>
          typeof value === "string" && value === "" ? [] : value,
        )
        .test(
          "not-empty",
          t("validations.required"),
          (value) => Array.isArray(value) && value.length > 0,
        )
        .required(t("validations.required")),

      // program parameters
      eligibilityConfirmation: yup
        .string()
        .oneOf([YesNoEnum.YES, YesNoEnum.NO], t("validations.required"))
        .required(t("validations.required")),
      consentForm: yup
        .string()
        .oneOf([YesNoEnum.YES, YesNoEnum.NO], t("validations.required"))
        .required(t("validations.required")),
      enableOnDemandIncentive: yup
        .string()
        .oneOf([YesNoEnum.YES, YesNoEnum.NO], t("validations.required"))
        .required(t("validations.required")),
      minAmount: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 }))
        .when("enableOnDemandIncentive", {
          is: (value: string) => value == YesNoEnum.YES,
          then: (schema) => schema.required(t("validations.required")),
          otherwise: (schema) => schema.notRequired(),
        }),
      maxAmount: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 }))
        .when("enableOnDemandIncentive", {
          is: (value: string) => value == YesNoEnum.YES,
          then: (schema) => schema.required(t("validations.required")),
          otherwise: (schema) => schema.notRequired(),
        }),
      maxEarnings: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      incentiveStructure: yup
        .string()
        .oneOf(
          [IncentiveStructureEnum["GIFTCARD-BASED"]],
          t("validations.required"),
        ),
      startDate: yup.string().required(t("validations.required")),
      endDate: yup.string(),
      programDays: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      // incentive ladder
      calculationMethod: yup
        .string()
        .oneOf(
          [
            CalculationMethodEnum["FIXED-AMOUNTS"],
            CalculationMethodEnum["INCREMENTAL-LADDER"],
            CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET"],
            CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET-AND-RECOVERY"],
          ],
          t("validations.required"),
        )
        .required(t("validations.required")),

      basedIncentiveNegativeResults: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? NaN
            : value,
        )
        .typeError(t("validations.required"))
        .required(t("validations.required"))
        .min(0, t("validations.min_value", { minValue: 0 })),
      basedIncentivePositiveResults: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? NaN
            : value,
        )
        .typeError(t("validations.required"))
        .required(t("validations.required"))
        .min(0, t("validations.min_value", { minValue: 0 })),
      incentiveAccountCreation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      incentiveFirstValidation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      incentiveValidationRequest: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      incentiveOnDemandValidation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      incentiveNonCarriage: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),
      incrementValueNegative: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? NaN
            : value,
        )
        .typeError(t("validations.invalid_format"))
        .min(0, t("validations.min_value", { minValue: 0 }))
        .when("calculationMethod", {
          is: (value: string) =>
            value == CalculationMethodEnum["INCREMENTAL-LADDER"] ||
            value == CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET"] ||
            value ==
              CalculationMethodEnum[
                "INCREMENTAL-LADDER-WITH-RESET-AND-RECOVERY"
              ],
          then: (schema) => schema.required(t("validations.required")),
          otherwise: (schema) => schema.notRequired(),
        }),
      maxIncentiveNegativeRe: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? NaN
            : value,
        )
        .typeError(t("validations.invalid_format"))
        .min(0, t("validations.min_value", { minValue: 0 }))
        .when("calculationMethod", {
          is: (value: string) =>
            value == CalculationMethodEnum["INCREMENTAL-LADDER"] ||
            value == CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET"] ||
            value ==
              CalculationMethodEnum[
                "INCREMENTAL-LADDER-WITH-RESET-AND-RECOVERY"
              ],
          then: (schema) => schema.required(t("validations.required")),
          otherwise: (schema) => schema.notRequired(),
        }),
      resetAmount: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? NaN
            : value,
        )
        .typeError(t("validations.invalid_format"))
        .min(0, t("validations.min_value", { minValue: 0 }))
        .when("calculationMethod", {
          is: (value: string) =>
            value == CalculationMethodEnum["INCREMENTAL-LADDER-WITH-RESET"] ||
            value ==
              CalculationMethodEnum[
                "INCREMENTAL-LADDER-WITH-RESET-AND-RECOVERY"
              ],
          then: (schema) => schema.required(t("validations.required")),
          otherwise: (schema) => schema.notRequired(),
        }),

      // validation rates
      validationRates: yup
        .string()
        .oneOf([ValidationRateEnum.WEEKDAYS], t("validations.required")),
      weekdays: yup
        .array()
        .of(yup.string())
        .transform((value) =>
          typeof value === "string" && value === "" ? [] : value,
        )
        .test(
          "not-empty",
          t("validations.required"),
          (value) => Array.isArray(value) && value.length > 0,
        )
        .required(t("validations.required")),

      // validations limits
      timeToSubmitValidation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? NaN
            : value,
        )
        .typeError(t("validations.required"))
        .required(t("validations.required"))
        .min(0, t("validations.min_value", { minValue: 0 })),
      validationLimitReminder: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      // contact
      fullName: yup
        .string()
        .max(100, t("validations.max_length", { count: 100 })),
      email: yup
        .string()
        .email(t("validations.invalid_format"))
        .max(100, t("validations.max_length", { count: 100 })),
      phoneNumber: yup
        .string()
        .test("phoneNumber", t("validations.invalid_format"), (value) => {
          if (!value || value.trim() === "") return true;
          const phoneRegex = /^\(?\d{4}\)?[-. ]?\d{6}$/;
          return phoneRegex.test(value);
        }),

      // re-enrollment
      numberOfDays: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      // form factors
      formFactors: yup.array().of(yup.string()).nullable(),

      // assign CM coordinator
      assignCMCoordinator: yup
        .string()
        .oneOf(
          [
            AssignCMCoordinatorEnum.HIDDEN,
            AssignCMCoordinatorEnum.OPTIONAL,
            AssignCMCoordinatorEnum.MANDATORY,
            "",
          ],
          t("validations.invalid_format"),
        )
        .nullable(),

      // enabled form factor 2
      enabledFormFactor2: yup.boolean().nullable(),

      // mobile fields visibility
      mobilePhoneNumber: yup
        .string()
        .oneOf(
          [
            FieldVisibilityEnum.HIDDEN,
            FieldVisibilityEnum.OPTIONAL,
            FieldVisibilityEnum.MANDATORY,
            "",
          ],
          t("validations.invalid_format"),
        )
        .nullable(),

      mobileDateOfBirth: yup
        .string()
        .oneOf(
          [
            FieldVisibilityEnum.HIDDEN,
            FieldVisibilityEnum.OPTIONAL,
            FieldVisibilityEnum.MANDATORY,
            "",
          ],
          t("validations.invalid_format"),
        )
        .nullable(),

      // validation request settings
      minutesValidationTimer: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      timerValidationRequestOpened: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      typeRequestValidation: yup.string().nullable(),

      distanceFilterGeofenceRequestValidation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      desiredAccuracy: yup.string().nullable(),

      showGeofenceChip: yup.boolean().nullable(),

      automaticGeofenceTrigger: yup.boolean().nullable(),

      enableValidationRequestSms: yup.boolean().nullable(),

      minutesTimeToCompleteValidation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      validationRequestMaxAttempts: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      enabledRewardFirstValidationSuccess: yup.boolean().nullable(),

      enabledRewardValidationRequestSuccess: yup.boolean().nullable(),

      enabledRewardOnDemandValidationSuccess: yup.boolean().nullable(),

      enableConsiderTimerOnMissedExpiration: yup.boolean().nullable(),

      enabledRewardNonCarriage: yup.boolean().nullable(),

      onDemandValidationRequest: yup.boolean().nullable(),

      onDemandValidationPeriodDays: yup
        .number()
        .nullable()
        .transform((value, original) => (original === "" ? undefined : value)),

      onDemandValidationDays: yup
        .array()
        .of(yup.number())
        .transform((value) =>
          typeof value === "string" && value === "" ? [] : value,
        )
        .nullable(),

      participantUserGuideURL: yup
        .array()
        .of(
          yup.object().shape({
            language: yup
              .string()
              .oneOf(["en-us", "en-gb", "es-419"]) // restrict to allowed languages
              .required(),
            userGuideURL: yup.string().url().required(),
          }),
        )
        .nullable(),

      educationalMaterials: yup
        .array()
        .of(yup.string())
        .transform((value) =>
          typeof value === "string" && value === "" ? [] : value,
        )
        .nullable(),

      // Geofence fields moved from parameters
      minutesPingGeofenceValidationRequest: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      metersGeofenceAccuracyThreshold: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      metersRadiusGeofenceRequestValidation: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      requestGeofenceValidationsPerDay: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      minimumMinsGeofenceBetweenRequestValidations: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      minutesPingGeofenceAlive: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 })),

      // New notification fields
      stickyAndroidNotificationTitle: yup.string().nullable(),

      stickyAndroidNotificationText: yup.string().nullable(),

      // COYN-1378: minimum image-classifier confidence (0-1) for a form-factor
      // positive to be accepted by the backend validation flow.
      minFormFactorConfidence: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 }))
        .max(1, t("validations.max_value", { maxValue: 1 })),

      // COYN-1494: minimum image-classifier confidence (0-1) that the study
      // code sticker is attached to the kit. Blank = record only, no gate.
      minStickerConfidence: yup
        .number()
        .transform((value, originalValue) =>
          typeof originalValue === "string" && originalValue?.trim() === ""
            ? null
            : value,
        )
        .nullable()
        .min(0, t("validations.min_value", { minValue: 0 }))
        .max(1, t("validations.max_value", { maxValue: 1 })),

      // configuration
      enabledRewardUponAccountCreation: yup.boolean().nullable(),

      // events
      events: yup
        .array()
        .of(
          yup.object().shape({
            eventId: yup.string(),
            validationRate: yup.string(),
            rewardConfiguration: yup.string(),
            validationLimits: yup.number().nullable(),
            validationReminders: yup.number().nullable(),
            needsCertificate: yup.boolean().required(t("validations.required")),
          }),
        )
        .nullable(),
    };

    return yup.object().shape(schemaFields);
  });
};

export type CreateProgramFormData = InferType<
  ReturnType<typeof createProgramSchema>
>;

export default createProgramSchema;
